import React, { useMemo } from 'react';
import { useRouter } from 'next/router';
import { FormattedMessage, useIntl } from 'react-intl';

import { isNotEmpty } from 'src/common/utils/arrayUtils';
import { StringCaseType, StringUtils } from 'src/common/utils/stringUtils';

import { getFilterSlugType } from '../../../helpers';
import { HeadingLocaleKey } from './constants';
import { Heading } from './InternalSearchExploreTab.sc';

export const SearchKeywordHeading: React.FC = () => {
  const router = useRouter();
  const intl = useIntl();
  const { keywords, filter } = router.query;

  const searchKeyword = useMemo(() => {
    return (
      StringUtils.convertOtherCaseToTitleCase(
        StringUtils.convertQueryToSingleString(keywords)
      ) ?? ''
    );
  }, [keywords]);

  const currentDate = useMemo(() => {
    const date = new Date();
    return {
      year: date.getFullYear(),
      month: intl.formatDate(date, { month: 'long' }),
    };
  }, [intl]);

  const headingContent = useMemo(() => {
    if (!keywords) {
      return null;
    }

    if (!filter) {
      return (
        <FormattedMessage
          {...HeadingLocaleKey.textTitleJobInCurrentTime}
          values={{
            keyword: searchKeyword,
            currentMonth: currentDate.month,
            currentYear: currentDate.year,
          }}
        />
      );
    }

    const filterString = StringUtils.convertQueryToSingleString(filter);

    const { filterSlug, isJobType, isEducationLevel, isLocationName } =
      getFilterSlugType(filterString);

    if (!filterSlug) {
      return (
        <FormattedMessage
          {...HeadingLocaleKey.textTitleJobInCurrentTime}
          values={{
            keyword: searchKeyword,
            currentMonth: currentDate.month,
            currentYear: currentDate.year,
          }}
        />
      );
    }

    const selectedFilterSlug =
      StringUtils.convertOtherCaseToTitleCase(
        filterSlug.toLowerCase(),
        StringCaseType.SnakeCase
      ) ?? '';

    if (isLocationName) {
      const locationName = selectedFilterSlug.substring(3);
      return (
        <FormattedMessage
          {...HeadingLocaleKey.textKeywordJobLocation}
          values={{
            keyword: searchKeyword,
            location: locationName,
            currentMonth: currentDate.month,
            currentYear: currentDate.year,
          }}
        />
      );
    }

    if (isEducationLevel) {
      return (
        <FormattedMessage
          {...HeadingLocaleKey.textKeywordEducationLevel}
          values={{
            keyword: searchKeyword,
            educationLevel: selectedFilterSlug,
            currentMonth: currentDate.month,
            currentYear: currentDate.year,
          }}
        />
      );
    }

    if (isJobType) {
      return (
        <FormattedMessage
          {...HeadingLocaleKey.textKeywordJobType}
          values={{
            keyword: searchKeyword,
            jobType: selectedFilterSlug,
            currentMonth: currentDate.month,
            currentYear: currentDate.year,
          }}
        />
      );
    }

    // Default case: work arrangement
    return (
      <FormattedMessage
        {...HeadingLocaleKey.textKeywordWorkArrangement}
        values={{
          keyword: searchKeyword,
          workArrangements: selectedFilterSlug,
          currentMonth: currentDate.month,
          currentYear: currentDate.year,
        }}
      />
    );
  }, [filter, searchKeyword, currentDate.month, currentDate.year, keywords]);

  if (!headingContent || !isNotEmpty(searchKeyword)) {
    return null;
  }

  return (
    <Heading className="job-search-key-word-results">{headingContent}</Heading>
  );
};
